/**
 * The materials catalogue and the price lists.
 *
 * Both are per-company data, not code — ARCHITECTURE.md's "Configurability without forks".
 * A price list with a `customer_id` is that customer's list; one without is company-wide.
 *
 * A device caches the list and computes an estimate; the server recomputes authoritatively on
 * apply (SYNC.md Rule 4). So a price edited here changes what the next job actually bills, and
 * the technician's screen says "تقديري" until it syncs.
 */

import { useMemo, useState } from "react";
import { formatIQD } from "@fanni/shared/money";
import { UNIT_AR } from "../lib/format.js";
import {
  useCreateMaterial,
  useCreatePriceList,
  useCustomers,
  useMaterials,
  usePriceLists,
  useSetPrice,
} from "../lib/queries.js";
import type { MaterialUnit } from "../lib/types.js";
import {
  Badge,
  Button,
  Card,
  Empty,
  ErrorNote,
  Field,
  Input,
  Loading,
  Modal,
  Note,
  PageHeader,
  Select,
  Table,
  Td,
  Th,
  Tr,
} from "../ui/kit.js";

export function Materials() {
  const materials = useMaterials();
  const priceLists = usePriceLists();
  const customers = useCustomers();
  const [addingMaterial, setAddingMaterial] = useState(false);
  const [addingList, setAddingList] = useState(false);
  const [q, setQ] = useState("");

  const rows = useMemo(() => {
    const all = materials.data?.materials ?? [];
    const term = q.trim();
    if (!term) return all;
    return all.filter((m) => m.nameAr.includes(term) || (m.sku ?? "").includes(term));
  }, [materials.data, q]);

  const lists = priceLists.data?.price_lists ?? [];
  const customerName = (id: string | null) =>
    id ? (customers.data?.customers.find((c) => c.id === id)?.name ?? id) : null;

  return (
    <>
      <PageHeader
        title="المواد والأسعار"
        subtitle="كتالوج الشركة وقوائم أسعارها. بيانات لا شيفرة — لكل شركة كتالوجها."
        actions={
          <>
            <Input placeholder="بحث" value={q} onChange={(e) => setQ(e.target.value)} className="w-48" />
            <Button size="sm" onClick={() => setAddingList(true)}>
              قائمة أسعار
            </Button>
            <Button size="sm" variant="primary" onClick={() => setAddingMaterial(true)}>
              مادة جديدة
            </Button>
          </>
        }
      />

      {materials.error ? <ErrorNote error={materials.error} className="mb-4" /> : null}

      <Card title="الكتالوج" className="mb-4" bodyClassName="">
        {materials.isLoading ? (
          <Loading />
        ) : rows.length === 0 ? (
          <Empty>لا توجد مواد. أضِف أول مادة ليتمكّن الفني من تسجيلها.</Empty>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>الاسم</Th>
                <Th>الرمز</Th>
                <Th>الوحدة</Th>
                <Th>الكلفة الافتراضية</Th>
                <Th>السعر الافتراضي</Th>
                <Th>الهامش</Th>
                <Th>تتبّع المخزون</Th>
              </tr>
            </thead>
            <tbody>
              {rows.map((m) => {
                const margin = m.defaultPriceFils - m.defaultCostFils;
                return (
                  <Tr key={m.id}>
                    <Td className="font-medium">{m.nameAr}</Td>
                    <Td>
                      <span className="num text-ink-soft">{m.sku ?? "—"}</span>
                    </Td>
                    <Td>{UNIT_AR[m.unit]}</Td>
                    <Td>
                      <span className="num">{formatIQD(m.defaultCostFils)}</span>
                    </Td>
                    <Td>
                      <span className="num">{formatIQD(m.defaultPriceFils)}</span>
                    </Td>
                    <Td>
                      <span className={margin < 0 ? "num font-medium text-bad" : "num text-good"}>
                        {formatIQD(margin)}
                      </span>
                    </Td>
                    <Td>
                      <Badge tone={m.trackStock ? "info" : "neutral"}>{m.trackStock ? "نعم" : "لا"}</Badge>
                    </Td>
                  </Tr>
                );
              })}
            </tbody>
          </Table>
        )}
        {/*
          The catalogue routes are POST-only: /catalog/materials has no PATCH and no DELETE, so
          a wrong price can be superseded by a customer price list but the row itself cannot be
          corrected from here.
        */}
        <p className="border-t border-line px-4 py-2 text-xs text-ink-faint">
          التعديل والحذف غير متاحين: منافذ الكتالوج في الـ API تدعم الإضافة فقط
          (<span className="num">POST</span>). لتصحيح سعر، عدّله عبر قائمة أسعار.
        </p>
      </Card>

      <div className="space-y-4">
        {lists.map((list) => (
          <PriceListCard
            key={list.id}
            list={list}
            customerName={customerName(list.customerId)}
            materials={materials.data?.materials ?? []}
          />
        ))}
        {lists.length === 0 && !priceLists.isLoading ? (
          <Card title="قوائم الأسعار">
            <Note>
              لا توجد قوائم أسعار. بدونها يستخدم الخادم السعر الافتراضي للمادة. أنشئ قائمة عامة
              للشركة، أو قائمة خاصة بزبون له اتفاق سعري مختلف.
            </Note>
          </Card>
        ) : null}
      </div>

      {addingMaterial ? <NewMaterial onClose={() => setAddingMaterial(false)} /> : null}
      {addingList ? <NewPriceList onClose={() => setAddingList(false)} /> : null}
    </>
  );
}

function PriceListCard({
  list,
  customerName,
  materials,
}: {
  list: { id: string; nameAr: string; isDefault: boolean; items: Array<{ id: string; materialId: string; unitPriceFils: number }> };
  customerName: string | null;
  materials: Array<{ id: string; nameAr: string; unit: MaterialUnit; defaultPriceFils: number }>;
}) {
  const setPrice = useSetPrice();
  const [editing, setEditing] = useState<string | null>(null);
  const [value, setValue] = useState("");

  const priced = new Map(list.items.map((i) => [i.materialId, i.unitPriceFils]));

  return (
    <Card
      title={
        <span className="flex items-center gap-2">
          {list.nameAr}
          {list.isDefault ? <Badge tone="info">الافتراضية</Badge> : null}
          {customerName ? <Badge tone="neutral">خاصة بـ {customerName}</Badge> : <Badge tone="neutral">عامة</Badge>}
        </span>
      }
      actions={
        <span className="num text-xs text-ink-faint">{list.items.length} سعر</span>
      }
      bodyClassName=""
    >
      {setPrice.error ? <ErrorNote error={setPrice.error} className="m-4" /> : null}
      <Table>
        <thead>
          <tr>
            <Th>المادة</Th>
            <Th>السعر الافتراضي</Th>
            <Th>سعر هذه القائمة</Th>
            <Th />
          </tr>
        </thead>
        <tbody>
          {materials.map((m) => {
            const price = priced.get(m.id);
            return (
              <Tr key={m.id}>
                <Td className="font-medium">
                  {m.nameAr} <span className="text-xs text-ink-faint">/ {UNIT_AR[m.unit]}</span>
                </Td>
                <Td>
                  <span className="num text-ink-soft">{formatIQD(m.defaultPriceFils)}</span>
                </Td>
                <Td>
                  {editing === m.id ? (
                    <Input
                      type="number"
                      dir="ltr"
                      autoFocus
                      value={value}
                      onChange={(e) => setValue(e.target.value)}
                      className="w-32"
                    />
                  ) : price === undefined ? (
                    <span className="text-xs text-ink-faint">— يستخدم الافتراضي —</span>
                  ) : (
                    <span className="num font-medium">{formatIQD(price)}</span>
                  )}
                </Td>
                <Td>
                  {editing === m.id ? (
                    <span className="flex gap-1">
                      <Button
                        size="sm"
                        variant="primary"
                        disabled={setPrice.isPending}
                        onClick={() =>
                          setPrice.mutate(
                            {
                              listId: list.id,
                              material_id: m.id,
                              unit_price_fils: Math.max(0, Math.round(Number(value) || 0)),
                            },
                            { onSuccess: () => setEditing(null) },
                          )
                        }
                      >
                        حفظ
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)}>
                        إلغاء
                      </Button>
                    </span>
                  ) : (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => {
                        setEditing(m.id);
                        setValue(String(price ?? m.defaultPriceFils));
                      }}
                    >
                      تحديد
                    </Button>
                  )}
                </Td>
              </Tr>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

function NewMaterial({ onClose }: { onClose: () => void }) {
  const create = useCreateMaterial();
  const [form, setForm] = useState({
    name_ar: "",
    sku: "",
    unit: "piece",
    default_cost_fils: "0",
    default_price_fils: "0",
    track_stock: false,
  });

  return (
    <Modal title="مادة جديدة" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}
      <div className="grid grid-cols-2 gap-3">
        <Field label="الاسم" className="col-span-2">
          <Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
        </Field>
        <Field label="الرمز (اختياري)">
          <Input dir="ltr" value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })} />
        </Field>
        <Field label="الوحدة">
          <Select value={form.unit} onChange={(e) => setForm({ ...form, unit: e.target.value })}>
            {Object.entries(UNIT_AR).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="الكلفة (فلس)" hint={`= ${formatIQD(Number(form.default_cost_fils) || 0)}`}>
          <Input
            type="number"
            dir="ltr"
            value={form.default_cost_fils}
            onChange={(e) => setForm({ ...form, default_cost_fils: e.target.value })}
          />
        </Field>
        <Field label="السعر (فلس)" hint={`= ${formatIQD(Number(form.default_price_fils) || 0)}`}>
          <Input
            type="number"
            dir="ltr"
            value={form.default_price_fils}
            onChange={(e) => setForm({ ...form, default_price_fils: e.target.value })}
          />
        </Field>
        <label className="col-span-2 flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.track_stock}
            onChange={(e) => setForm({ ...form, track_stock: e.target.checked })}
          />
          تتبّع المخزون
        </label>
      </div>
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending || !form.name_ar.trim()}
        onClick={() =>
          create.mutate(
            {
              name_ar: form.name_ar.trim(),
              sku: form.sku.trim() || undefined,
              unit: form.unit,
              default_cost_fils: Math.max(0, Math.round(Number(form.default_cost_fils) || 0)),
              default_price_fils: Math.max(0, Math.round(Number(form.default_price_fils) || 0)),
              track_stock: form.track_stock,
            },
            { onSuccess: onClose },
          )
        }
      >
        حفظ
      </Button>
    </Modal>
  );
}

function NewPriceList({ onClose }: { onClose: () => void }) {
  const create = useCreatePriceList();
  const customers = useCustomers();
  const [form, setForm] = useState({ name_ar: "", is_default: false, customer_id: "" });

  return (
    <Modal title="قائمة أسعار جديدة" onClose={onClose}>
      {create.error ? <ErrorNote error={create.error} className="mb-3" /> : null}
      <div className="space-y-3">
        <Field label="الاسم">
          <Input value={form.name_ar} onChange={(e) => setForm({ ...form, name_ar: e.target.value })} />
        </Field>
        <Field label="خاصة بزبون (اختياري)" hint="اتركها فارغة لقائمة عامة للشركة">
          <Select value={form.customer_id} onChange={(e) => setForm({ ...form, customer_id: e.target.value })}>
            <option value="">— قائمة عامة —</option>
            {(customers.data?.customers ?? []).map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </Select>
        </Field>
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={form.is_default}
            onChange={(e) => setForm({ ...form, is_default: e.target.checked })}
          />
          اجعلها القائمة الافتراضية
        </label>
      </div>
      <Button
        variant="primary"
        className="mt-4 w-full"
        disabled={create.isPending || !form.name_ar.trim()}
        onClick={() =>
          create.mutate(
            {
              name_ar: form.name_ar.trim(),
              is_default: form.is_default,
              customer_id: form.customer_id || null,
            },
            { onSuccess: onClose },
          )
        }
      >
        حفظ
      </Button>
    </Modal>
  );
}
